import { AxiosError, CanceledError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getRetryPredicate } from 'components/status-checks/common/observability-tool-authentication-utils'
import { type UseValidateEndpointConfiguration } from 'components/status-checks/hooks/types'
import {
  combineMutationResponseStatusFlags,
  USE_COMBINE_MUTATION_RESULT_FLAGS
} from 'components/status-checks/hooks/use-validate-configuration-utils'
import useAuth from 'hooks/use-auth'
import {
  type PniRunTestEndpointRequestParams,
  type RunTestEndpointResponse
} from 'queries/external-integrations/types'
import { useCreateWebhookInvocation } from 'queries/integration-invocations/use-create-webook-invocation'
import { useIntegrationInvocationsRunTestEndpoint } from 'queries/integration-invocations/use-integration-invocations-run-test-endpoint'
import { useStatusCheckRunTestEndpoint } from 'queries/status-checks/use-status-check-run-test-endpoint'
import {
  buildTeamExternalEndpointConfigurationRequest,
  getEndpointConfigurationUrl,
  teamExternalIntegrationsObject
} from 'utils/team-external-integrations/team-external-integrations-util'

/**
 * Validates the endpoint configuration for a given Team External Integration by executing
 * a test endpoint request. Handles both Private Network Integrations (PNI) and public
 * integrations by utilizing the appropriate hooks and methods.
 */
export function useValidateEndpointConfiguration(
  abortController?: AbortController
): UseValidateEndpointConfiguration {
  const { currentTeamId } = useAuth()
  const [isCanceled, setIsCanceled] = useState(false)
  const [isPNI, setIsPNI] = useState(false)

  // Hooks used to execute the endpoint test for authentication validation (PNI)
  const {
    mutateAsync: createWebhookInvocation,
    reset: resetCreateWebhookInvocation,
    ...createWebhookInvocationResponse
  } = useCreateWebhookInvocation(abortController?.signal)

  const {
    mutateAsync: integrationInvocationRunTestEndpoint,
    reset: resetIntegrationInvocationRunTestEndpoint,
    ...integrationInvocationRunTestEndpointResponse
  } = useIntegrationInvocationsRunTestEndpoint(abortController?.signal, {
    retry: getRetryPredicate
  })

  // Hook used to execute the endpoint test for authentication validation (Public)
  const {
    mutateAsync: statusCheckRunTestEndpoint,
    reset: resetStatusCheckRunTestEndpoint,
    ...statusCheckRunTestEndpointResponse
  } = useStatusCheckRunTestEndpoint(abortController?.signal)

  /*
   * Combine the chain of query responses to derive the proper query status flag values.
   * Some flags use 'OR' logic, others use 'AND' logic.
   */
  const { isError, isIdle, isLoading, isSuccess } =
    combineMutationResponseStatusFlags(
      USE_COMBINE_MUTATION_RESULT_FLAGS,
      isPNI
        ? [
            createWebhookInvocationResponse,
            integrationInvocationRunTestEndpointResponse
          ]
        : [statusCheckRunTestEndpointResponse]
    )

  // Ensure we don't have any lingering run-test-endpoint requests if hook is unmounted
  useEffect(() => {
    return () => {
      abortController?.abort()
    }
  }, [abortController])

  const resetAllMutations = useCallback(() => {
    // reset query responses
    resetCreateWebhookInvocation()
    resetIntegrationInvocationRunTestEndpoint()
    resetStatusCheckRunTestEndpoint()

    // reset local state flags
    setIsPNI(false)
    setIsCanceled(false)
  }, [
    resetCreateWebhookInvocation,
    resetIntegrationInvocationRunTestEndpoint,
    resetStatusCheckRunTestEndpoint
  ])

  /**
   * Handles submitting a chain of requests in the PNI case, or a singular request in the public case,
   * which validates the provided Authentication credentials. Manages error handling and state updates.
   */
  const validateEndpointConfiguration = useCallback<
    UseValidateEndpointConfiguration['validateEndpointConfiguration']
  >(
    async (
      integration,
      {
        authenticate = true,
        endpointConfiguration,
        statusCheckId,
        statusCheckMultiSelectTags,
        teamId
      } = {}
    ) => {
      setIsCanceled(false)
      setIsPNI(false)

      const evaluatedTeamId = teamId ?? currentTeamId

      const { authUrl, isPrivateNetworkIntegration, type } = integration
      const endpointConfigurationUrl =
        authUrl ||
        (await getEndpointConfigurationUrl(integration, evaluatedTeamId)) ||
        teamExternalIntegrationsObject[type].authUrl
      let endpointResponse: RunTestEndpointResponse

      // Initial Step: Validate the endpoint configuration
      try {
        if (!endpointConfigurationUrl) {
          throw new AxiosError(
            `Endpoint configuration url (${endpointConfigurationUrl}) is not a valid url`
          )
        }

        const requestParams = buildTeamExternalEndpointConfigurationRequest(
          integration,
          endpointConfigurationUrl,
          statusCheckId,
          authenticate,
          endpointConfiguration
        )

        /*
         * Pre-flight checks for both PNI and non-PNI cases. This is required to ensure the provided credentials
         * are valid before we attempt to save the authentication. Updates the response state if an error is encountered.
         * This allows the UI to render the appropriate error messaging details.
         */

        if (isPrivateNetworkIntegration) {
          setIsPNI(true)

          const pniRequestParams: PniRunTestEndpointRequestParams = {
            ...requestParams,
            multiSelectTags: statusCheckMultiSelectTags ?? {}
          }

          // Creates the webhook invocation which is then used to test the endpoint response
          const { invocationId } = await createWebhookInvocation(
            pniRequestParams
          )

          if (!invocationId)
            throw new AxiosError(
              'Failed to create webhook invocation while testing the endpoint configuration'
            )

          /*
           * `mutationFn` can only accept a single typed parameter, so we use a data structure to
           * compartmentalize the required parameterized information.
           */
          endpointResponse = await integrationInvocationRunTestEndpoint([
            invocationId,
            pniRequestParams
          ])
        } else {
          endpointResponse = await statusCheckRunTestEndpoint(requestParams)
        }

        return Promise.resolve(endpointResponse)
      } catch (error: unknown) {
        if (error instanceof CanceledError) {
          setIsCanceled(true)
        }

        return Promise.reject(error)
      }
    },
    [
      createWebhookInvocation,
      currentTeamId,
      integrationInvocationRunTestEndpoint,
      statusCheckRunTestEndpoint
    ]
  )

  return useMemo(
    () => ({
      isCanceled,
      isError,
      isIdle,
      isLoading,
      isSuccess,
      reset: resetAllMutations,
      validateEndpointConfiguration
    }),
    [
      isCanceled,
      isError,
      isIdle,
      isLoading,
      isSuccess,
      resetAllMutations,
      validateEndpointConfiguration
    ]
  )
}
